import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  FormField,
  FormItem,
  FormControl,
  FormLabel,
  FormDescription,
} from "../ui/form";
import { Input } from "../ui/input";
import { CheckCircle } from "@phosphor-icons/react";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

export type CommType = {
  value: string;
  group: string;
};

interface CommunicationTypeSelectorProps {
  groupTypes: CommType[];
  currentGroup: string;
  otherType: string;
}

function CommunicationTypeSelector({
  groupTypes,
  currentGroup,
  otherType,
}: CommunicationTypeSelectorProps) {
  const { t } = useTranslation(["communicationGenerator", "common"]);
  const form = useFormContext();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [showAll, setShowAll] = useState(false);

  const filteredTypes = groupTypes.filter(
    (comm) => comm.group === currentGroup
  );
  const formType = form.watch("type");

  // Determine which types to show based on mobile/desktop and showAll state
  const typesToShow = isDesktop
    ? filteredTypes
    : showAll
      ? filteredTypes
      : filteredTypes.slice(0, 4); // Show first 4 on mobile when not expanded

  const shouldShowExpandButton =
    !isDesktop && !showAll && filteredTypes.length > 4;

  return (
    <FormField
      control={form.control}
      name="type"
      render={({ field }) => (
        <FormItem className="mt-4">
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              defaultValue={field.value}
              className="flex flex-col flex-wrap w-full gap-1"
            >
              <div className="flex flex-row flex-wrap gap-2">
                {typesToShow.map((comm) => {
                  if (isDesktop) {
                    return (
                      <FormItem key={comm.value}>
                        <FormControl>
                          <RadioGroupItem
                            value={comm.value}
                            className="hidden"
                          />
                        </FormControl>
                        <FormLabel
                          className={cn(
                            "px-3 py-2 font-light bg-card rounded-lg transition-all text-wrap @sm:whitespace-nowrap cursor-pointer border flex flex-row items-center h-full",
                            formType === comm.value ||
                              (comm.value === otherType &&
                                !groupTypes
                                  .map((t) => t.value)
                                  .includes(formType))
                              ? "bg-secondary !font-semibold text-dark-grey border-transparent"
                              : "",
                            "!text-small-content !leading-[normal]"
                          )}
                        >
                          <span
                            className={cn(
                              "inline-flex items-center justify-center mr-2 transition-[width] duration-200 ease-in-out",
                              formType === comm.value ||
                                (comm.value === otherType &&
                                  !groupTypes
                                    .map((t) => t.value)
                                    .includes(formType))
                                ? "opacity-100 w-5 h-5"
                                : "opacity-0 w-0 h-0 mr-0"
                            )}
                          >
                            <CheckCircle weight="bold" className="font-bold" />
                          </span>

                          {t(comm.value)}
                        </FormLabel>
                      </FormItem>
                    );
                  } else {
                    return (
                      <FormItem key={comm.value} className="py-2 pr-2">
                        <FormControl>
                          <RadioGroupItem
                            value={comm.value}
                            className="hidden"
                          />
                        </FormControl>
                        <FormLabel
                          className={cn(
                            "px-3 py-2 font-light bg-card rounded-lg whitespace-nowrap transition-colors cursor-pointer",
                            form.watch("type") === comm.value &&
                              "bg-secondary font-semibold text-secondary-foreground border-secondary-foreground",
                            "!text-small-content cursor-pointer"
                          )}
                        >
                          {form.watch("type") === comm.value && (
                            <CheckCircle
                              weight="bold"
                              className="inline h-full pb-[2px] mr-2 font-bold aspect-square"
                            />
                          )}
                          {t(comm.value)}
                        </FormLabel>
                      </FormItem>
                    );
                  }
                })}

                {shouldShowExpandButton && (
                  <motion.button
                    layout
                    type="button"
                    className="px-3 py-2 my-2 text-xs font-light transition-colors rounded-lg cursor-pointer bg-card whitespace-nowrap"
                    onClick={() => setShowAll(true)}
                  >
                    {t("showAll")}
                  </motion.button>
                )}
              </div>
              {(!groupTypes.map((t) => t.value).includes(form.watch("type")) ||
                form.watch("type") === otherType) && (
                <FormField
                  key="other-type"
                  control={form.control}
                  name="type"
                  render={({ field: { value, ...field } }) => (
                    <FormItem className="mt-4">
                      <FormLabel className="!text-form-title leading-normal">
                        {t("commType.other.label")}
                      </FormLabel>
                      <FormDescription className="text-card-content !text-light-grey  mb-4">
                        {t("commType.other.description")}
                      </FormDescription>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("commType.other.placeholder")}
                          value={value === otherType ? "" : value}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </RadioGroup>
          </FormControl>
        </FormItem>
      )}
    />
  );
}

export default CommunicationTypeSelector;
